import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import {
  epelsaConfigService,
  epelsaLineasNoVinculadasService,
} from '../../../services/epelsaService';
import { almacenesService } from '../../../services/tiendaService';
import { productosService } from '../../../services/tiendaService';

const ACCENT = '#34D399';
const GOLD = '#D6EF4E';

const fmtDate = (d) => d ? new Date(d)?.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const PROTOCOLOS = ['REST', 'TCP/IP', 'ARCHIVO', 'LOCAL'];

const EpelsaConfiguracion = () => {
  const [config, setConfig] = useState(null);
  const [almacenes, setAlmacenes] = useState([]);
  const [lineasNoVinculadas, setLineasNoVinculadas] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({
    nombre: 'Báscula Principal',
    modelo: '',
    ip: '',
    puerto: 80,
    protocolo: 'REST',
    codigo_bascula: '',
    almacen_id: '',
    caja_diaria_referencia: '',
    recepcion_automatica: false,
    observaciones: '',
  });
  const [vinculandoId, setVinculandoId] = useState(null);
  const [productoSeleccionado, setProductoSeleccionado] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [cfg, alms, lineas, prods] = await Promise.all([
        epelsaConfigService?.get(),
        almacenesService?.getAll(),
        epelsaLineasNoVinculadasService?.getAll(),
        productosService?.getAll({ activo: true }),
      ]);
      setConfig(cfg);
      setAlmacenes(alms);
      setLineasNoVinculadas(lineas);
      setProductos(prods);
      if (cfg) {
        setForm({
          nombre: cfg?.nombre || 'Báscula Principal',
          modelo: cfg?.modelo || '',
          ip: cfg?.ip || '',
          puerto: cfg?.puerto || 80,
          protocolo: cfg?.protocolo || 'REST',
          codigo_bascula: cfg?.codigo_bascula || '',
          almacen_id: cfg?.almacen_id || '',
          caja_diaria_referencia: cfg?.caja_diaria_referencia || '',
          recepcion_automatica: cfg?.recepcion_automatica || false,
          observaciones: cfg?.observaciones || '',
        });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e?.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const payload = {
        ...form,
        puerto: parseInt(form?.puerto) || 80,
        almacen_id: form?.almacen_id || null,
        id: config?.id,
      };
      const updated = await epelsaConfigService?.upsert(payload);
      setConfig(updated);
      setMsg({ type: 'ok', text: 'Configuración guardada correctamente.' });
    } catch (e) {
      setMsg({ type: 'error', text: e?.message });
    } finally {
      setSaving(false);
    }
  };

  const handleVincular = async (lineaId) => {
    const productoId = productoSeleccionado?.[lineaId];
    if (!productoId) return setMsg({ type: 'warn', text: 'Selecciona un producto antes de vincular.' });
    setVinculandoId(lineaId);
    try {
      await epelsaLineasNoVinculadasService?.vincular(lineaId, productoId);
      setMsg({ type: 'ok', text: 'Producto vinculado. La vinculación se recordará para futuros tickets.' });
      await loadData();
    } catch (e) {
      setMsg({ type: 'error', text: e?.message });
    } finally {
      setVinculandoId(null);
    }
  };

  const handleIgnorar = async (lineaId) => {
    try {
      await epelsaLineasNoVinculadasService?.ignorar(lineaId);
      setMsg({ type: 'ok', text: 'Línea marcada como ignorada.' });
      await loadData();
    } catch (e) {
      setMsg({ type: 'error', text: e?.message });
    }
  };

  const msgStyle = msg?.type === 'ok'
    ? { bg: 'rgba(52,211,153,0.08)', border: 'rgba(52,211,153,0.25)', color: '#34D399', icon: 'CheckCircle' }
    : msg?.type === 'warn'
      ? { bg: 'rgba(251,191,36,0.08)', border: 'rgba(251,191,36,0.25)', color: '#FBBF24', icon: 'AlertTriangle' }
      : { bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.25)', color: '#F87171', icon: 'AlertCircle' };

  const inputClass = "w-full px-3 py-2.5 rounded-xl text-sm font-oranienbaum text-white/80 placeholder-white/25 outline-none";
  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' };
  const labelClass = "font-oranienbaum text-white/50 text-xs mb-1 block";

  return (
    <div className="space-y-6">
      {/* Mensaje */}
      {msg && (
        <div className="flex items-start gap-3 p-4 rounded-xl" style={{ background: msgStyle?.bg, border: `1px solid ${msgStyle?.border}` }}>
          <Icon name={msgStyle?.icon} size={16} style={{ color: msgStyle?.color, flexShrink: 0, marginTop: 1 }} />
          <p className="font-oranienbaum text-sm" style={{ color: msgStyle?.color }}>{msg?.text}</p>
          <button onClick={() => setMsg(null)} className="ml-auto opacity-60 hover:opacity-100"><Icon name="X" size={14} style={{ color: msgStyle?.color }} /></button>
        </div>
      )}

      {/* Formulario de configuración */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <h3 className="font-oranienbaum font-semibold text-white text-base mb-4">Parámetros de la báscula</h3>
        {loading ? (
          <div className="flex items-center gap-3 py-6">
            <div className="w-5 h-5 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
            <span className="font-oranienbaum text-white/40 text-sm">Cargando configuración...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Nombre de la báscula</label>
              <input className={inputClass} style={inputStyle} value={form?.nombre} onChange={e => setForm(f => ({ ...f, nombre: e?.target?.value }))} placeholder="Báscula Principal" />
            </div>
            <div>
              <label className={labelClass}>Modelo</label>
              <input className={inputClass} style={inputStyle} value={form?.modelo} onChange={e => setForm(f => ({ ...f, modelo: e?.target?.value }))} placeholder="Ej: Epelsa EK-6000" />
            </div>
            <div>
              <label className={labelClass}>Protocolo de comunicación</label>
              <select className={inputClass} style={inputStyle} value={form?.protocolo} onChange={e => setForm(f => ({ ...f, protocolo: e?.target?.value }))}>
                {PROTOCOLOS?.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Dirección IP</label>
              <input className={inputClass} style={inputStyle} value={form?.ip} onChange={e => setForm(f => ({ ...f, ip: e?.target?.value }))} placeholder="192.168.1.100" />
            </div>
            <div>
              <label className={labelClass}>Puerto</label>
              <input type="number" className={inputClass} style={inputStyle} value={form?.puerto} onChange={e => setForm(f => ({ ...f, puerto: e?.target?.value }))} placeholder="80" />
            </div>
            <div>
              <label className={labelClass}>Código de báscula</label>
              <input className={inputClass} style={inputStyle} value={form?.codigo_bascula} onChange={e => setForm(f => ({ ...f, codigo_bascula: e?.target?.value }))} placeholder="EPELSA-01" />
            </div>
            <div>
              <label className={labelClass}>Almacén asociado</label>
              <select className={inputClass} style={inputStyle} value={form?.almacen_id} onChange={e => setForm(f => ({ ...f, almacen_id: e?.target?.value }))}>
                <option value="">— Sin almacén —</option>
                {almacenes?.map(a => <option key={a?.id} value={a?.id}>{a?.nombre}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Caja diaria asociada</label>
              <input className={inputClass} style={inputStyle} value={form?.caja_diaria_referencia} onChange={e => setForm(f => ({ ...f, caja_diaria_referencia: e?.target?.value }))} placeholder="EPELSA-01" />
            </div>
            <div className="flex items-center gap-3 pt-5">
              <button
                onClick={() => setForm(f => ({ ...f, recepcion_automatica: !f?.recepcion_automatica }))}
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-oranienbaum transition-all"
                style={form?.recepcion_automatica
                  ? { background: `${ACCENT}15`, border: `1px solid ${ACCENT}35`, color: ACCENT }
                  : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.45)' }}
              >
                <Icon name={form?.recepcion_automatica ? 'ToggleRight' : 'ToggleLeft'} size={16} />
                Recepción automática
              </button>
            </div>
            <div className="sm:col-span-2 lg:col-span-3">
              <label className={labelClass}>Observaciones</label>
              <textarea className={inputClass} style={inputStyle} rows={2} value={form?.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e?.target?.value }))} placeholder="Notas adicionales sobre la configuración..." />
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 text-white/30 text-xs font-oranienbaum">
            <Icon name="Clock" size={12} />
            <span>Última conexión: {fmtDate(config?.ultima_conexion)}</span>
          </div>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-oranienbaum font-semibold transition-all disabled:opacity-50"
            style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}35`, color: ACCENT }}
          >
            {saving ? <div className="w-4 h-4 rounded-full border-2 border-[#34D399]/30 border-t-[#34D399] animate-spin" /> : <Icon name="Save" size={15} />}
            Guardar configuración
          </button>
        </div>
      </div>

      {/* Líneas no vinculadas */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2">
            <Icon name="AlertTriangle" size={15} style={{ color: GOLD }} />
            <span className="font-oranienbaum text-white/60 text-sm font-semibold">Productos no vinculados</span>
          </div>
          <span className="font-oranienbaum text-white/30 text-xs">{lineasNoVinculadas?.length} pendientes</span>
        </div>
        {lineasNoVinculadas?.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${ACCENT}10`, border: `1px solid ${ACCENT}20` }}>
                <Icon name="CheckCircle" size={18} style={{ color: ACCENT }} />
              </div>
              <p className="font-oranienbaum text-white/40 text-sm">Todos los productos están vinculados</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-max">
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Ticket', 'PLU', 'EAN', 'Nombre Epelsa', 'Importe', 'Vincular con producto', 'Acción']?.map(col => (
                    <th key={col} className="px-3 py-3 text-left">
                      <span className="font-oranienbaum text-white/40 text-xs uppercase tracking-wider">{col}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lineasNoVinculadas?.map((linea, i) => (
                  <tr key={linea?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{linea?.numero_ticket}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-xs">{linea?.plu || '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-xs">{linea?.ean || '—'}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/80 text-sm">{linea?.nombre_epelsa}</span></td>
                    <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{linea?.importe ? `${linea?.importe?.toFixed(2)} €` : '—'}</span></td>
                    <td className="px-3 py-3 min-w-[200px]">
                      <select
                        className="w-full px-2 py-1.5 rounded-lg text-xs font-oranienbaum text-white/70 outline-none"
                        style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}
                        value={productoSeleccionado?.[linea?.id] || ''}
                        onChange={e => setProductoSeleccionado(prev => ({ ...prev, [linea?.id]: e?.target?.value }))}
                      >
                        <option value="">— Seleccionar producto —</option>
                        {productos?.map(p => <option key={p?.id} value={p?.id}>{p?.nombre} ({p?.codigo_interno || p?.ean || '—'})</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleVincular(linea?.id)}
                          disabled={vinculandoId === linea?.id}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all disabled:opacity-50"
                          style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30`, color: ACCENT }}
                        >
                          {vinculandoId === linea?.id ? '...' : 'Vincular'}
                        </button>
                        <button
                          onClick={() => handleIgnorar(linea?.id)}
                          className="px-2.5 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
                          style={{ background: 'rgba(248,113,113,0.10)', border: '1px solid rgba(248,113,113,0.25)', color: '#F87171' }}
                        >
                          Ignorar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Información necesaria para conectar físicamente */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(96,165,250,0.05)', border: '1px solid rgba(96,165,250,0.15)' }}>
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Info" size={16} style={{ color: '#60A5FA' }} />
          <h3 className="font-oranienbaum font-semibold text-white/80 text-sm">Información necesaria para conectar físicamente la báscula</h3>
        </div>
        <div className="space-y-2">
          {[
            'Protocolo exacto soportado por el modelo de báscula (REST API, TCP/IP, exportación de archivo, integración local)',
            'Dirección IP de la báscula en la red local o URL del endpoint REST',
            'Puerto de comunicación y credenciales de acceso si el protocolo lo requiere',
            'Formato exacto del payload del ticket (campos, tipos de datos, codificación)',
            'Método de autenticación: token, usuario/contraseña, certificado, o sin autenticación',
            'Frecuencia de envío de tickets: push en tiempo real, polling, o exportación periódica',
            'Documentación técnica del fabricante Epelsa para el modelo específico',
            'Acceso a la red local donde está conectada la báscula (VPN si es remoto)',
          ]?.map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: '#60A5FA' }} />
              <p className="font-oranienbaum text-white/55 text-xs leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
        <p className="font-oranienbaum text-white/30 text-xs mt-3 pt-3" style={{ borderTop: '1px solid rgba(96,165,250,0.10)' }}>
          La interfaz y el backend están preparados para recibir tickets reales. Solo es necesario implementar el adaptador del protocolo específico de la báscula.
        </p>
      </div>
    </div>
  );
};

export default EpelsaConfiguracion;
